import { Avatar, Button, Typography } from "@/components/kit";
import { useState, ReactNode } from "react";
import SvgIcon from "@/components/common/SvgIcon";
import Stepper from "@/components/common/AddFlowStepper";

interface Step {
  stepNumber: number;
  title: string;
  component: ReactNode;
  onSubmit?: () => void | Promise<void>;
  finishButtonText?: string;
}

interface MultiStepFormLayoutProps {
  title: string;
  description: ReactNode;
  saveButtonText?: string;
  finishButtonText?: string;
  steps: Step[];
  onSubmit: () => void;
  isValid?: boolean;
  showAvatar?: boolean;
  avatarSize?: "sm" | "md" | "lg";
  isLoading?: boolean;
  showSaveButton?: boolean;
  isNextDisabled?: boolean | ((step: number) => boolean);
  showCancelButton?: boolean;
  onCancel?: () => void;
  avatarSrc?: string;
  iconName?: string;
  image?: ReactNode;
  endAdornment?: ReactNode | string;
}

function MultiStepFormLayout({
  title,
  description,
  saveButtonText,
  finishButtonText = "Finish & Save",
  steps,
  onSubmit,
  isValid = true,
  showAvatar = true,
  showSaveButton = true,
  showCancelButton = false,
  isNextDisabled = true,
  avatarSize = "lg",
  isLoading,
  onCancel,
  avatarSrc,
  image,
  iconName,
  endAdornment,
}: MultiStepFormLayoutProps) {
  const [currentStep, setCurrentStep] = useState(1);

  const currentStepData = steps.find((step) => step.stepNumber === currentStep);
  const totalSteps = steps.length;

  const handleNextOrSubmit = async () => {
    // If current step has its own submit handler, call it
    if (currentStepData?.onSubmit) {
      await currentStepData.onSubmit();
      // After successful submit, move to next step
      setCurrentStep((prev) => Math.min(totalSteps, prev + 1));
    } else if (currentStep !== totalSteps) {
      // Just move to next step if no submit handler
      setCurrentStep((prev) => Math.min(totalSteps, prev + 1));
    } else {
      // Last step without its own handler, use main onSubmit
      onSubmit();
    }
  };

  const getNextButtonText = () => {
    if (currentStepData?.finishButtonText) {
      return currentStepData?.finishButtonText;
    }
    return "Next";
  };

  return (
    <div className="flex gap-8  flex-col p-5 w-full h-full">
      <div className="w-full flex gap-3">
        {image && <>{image}</>}
        {showAvatar && !iconName && (
          <Avatar src={avatarSrc} size={avatarSize} />
        )}
        {iconName && (
          <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded-full">
            <SvgIcon
              className="w-8 h-8 "
              fill="fill-gray-800 fill-[#414488]"
              iconName={iconName}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Typography variant="Title3-Bold">{title}</Typography>
          <Typography color="text-gray-500" variant="Body1-Regular">
            {description}
          </Typography>
        </div>
        <div className="flex-1" />
        {showCancelButton && (
          <Button variant="outlined" onClick={() => onCancel && onCancel()}>
            Cancel
          </Button>
        )}
        {showSaveButton && (
          <Button
            isLoading={isLoading}
            disabled={!isValid}
            onClick={onSubmit}
            type="submit"
          >
            {saveButtonText}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-[80px] h-full px-6 min-h-0">
        <div className="w-full flex flex-col justify-between">
          <Stepper currentStep={currentStep}>
            {steps.map((step) => (
              <Stepper.Step
                key={step.stepNumber}
                stepNumber={step.stepNumber}
                title={step.title}
              />
            ))}
          </Stepper>
          {!!endAdornment && <>{endAdornment}</>}
        </div>

        <div className="w-full col-span-3 min-h-0">
          <div className="max-w-[1000px] h-full m-auto w-full flex flex-col min-h-0">
            <div className="flex-1 overflow-auto content-start w-full min-h-0 gap-4 flex flex-col pe-2">
              {currentStepData?.component}
            </div>

            <div className="px-10 py-3 flex justify-between">
              <Button
                disabled={currentStep === 1}
                onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
                variant="outlined"
              >
                <SvgIcon
                  iconName="arrowRightLine"
                  fill="fill-gray-800"
                  className="w-4 h-4 rotate-180"
                />
                Back
              </Button>

              {currentStep !== totalSteps ? (
                <Button
                  onClick={handleNextOrSubmit}
                  variant="outlined"
                  disabled={
                    typeof isNextDisabled === "boolean"
                      ? !isNextDisabled
                      : !isNextDisabled(currentStep)
                  }
                  isLoading={isLoading}
                >
                  {getNextButtonText()}
                  <SvgIcon
                    iconName="arrowRightLine"
                    fill="fill-gray-800"
                    className="w-4 h-4"
                  />
                </Button>
              ) : (
                <Button
                  disabled={!isValid}
                  onClick={onSubmit}
                  isLoading={isLoading}
                >
                  {finishButtonText}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MultiStepFormLayout;
